import { TRAFFIC_CAPS, type TrafficTopRow } from '@bartizo/shared';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { lookupAddresses } from '../api/addresses.js';
import { getTrafficTop, type TrafficScope } from '../api/traffic.js';
import { blockablePath, blockRuleLink } from '../lib/block-page.js';
import {
  RISK_RANK,
  SUSPECT_LABELS,
  suspectsOf,
  type Suspect,
  type SuspectType,
} from '../lib/traffic-filter.js';
import type { TrafficRisk } from '../lib/traffic-risk.js';
import { Card } from '../ui/Card.js';
import { DataTable, type Column } from '../ui/DataTable.js';
import { SelectField } from '../ui/Field.js';
import { EmptyState } from './EmptyState.js';
import { RiskBadge } from './StatusBadge.js';
import { BanAction, BotActions, KeyCell, type Actor } from './traffic-parts.js';
import { SiteName } from './TrafficSites.js';
import { TrafficRowDetail } from './TrafficRowDetail.js';

/**
 * What is worth a look (P11.15): the risky rows of the address, page, bot and site
 * lists in one table, worst first, each with the reason in words and the button
 * that deals with it. It is the table to read first; the explorer below it is for
 * everything else.
 *
 * Risk is the reading the lists already carry (`trafficRisk`): refusals, failures
 * and a bot that is not who it says. It says what a row looks like, not what it is,
 * so the reason is always written beside it.
 */

const TYPES: readonly SuspectType[] = ['ip', 'path', 'bot', 'host'];
const SHOWN = 10;

const RISKS: readonly { value: TrafficRisk; label: string }[] = [
  { value: 'medium', label: 'Medium or worse' },
  { value: 'high', label: 'High or worse' },
  { value: 'critical', label: 'Critical only' },
];

export function TrafficWorthALook({
  scope,
  actor,
  canChange,
}: {
  scope: TrafficScope;
  actor: Actor | undefined;
  canChange: boolean;
}): ReactNode {
  const [type, setType] = useState<SuspectType | 'all'>('all');
  const [minRisk, setMinRisk] = useState<TrafficRisk>('medium');
  const [notStopped, setNotStopped] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const results = useQueries({
    queries: TYPES.map((dimension) => ({
      queryKey: ['traffic-top', scope, dimension, 'worth-a-look'],
      queryFn: () => getTrafficTop(scope, dimension, TRAFFIC_CAPS[dimension]),
      placeholderData: keepPreviousData,
    })),
  });
  const loaded = results.every((each) => each.data !== undefined);
  const lists = Object.fromEntries(
    TYPES.map((each, index) => [each, results[index]?.data?.rows ?? []]),
  ) as Record<SuspectType, TrafficTopRow[]>;
  const found = suspectsOf(lists, minRisk);

  // Whether each risky address is stopped: asked only for the addresses in the list.
  const addresses = found.filter((each) => each.type === 'ip').map((each) => each.row.key);
  const statuses = useQuery({
    queryKey: ['address-lookup', addresses],
    queryFn: () => lookupAddresses(addresses),
    enabled: addresses.length > 0,
  });
  const statusOf = new Map((statuses.data ?? []).map((entry) => [entry.ip, entry]));

  const visible = found.filter((each) => {
    if (type !== 'all' && each.type !== type) return false;
    if (notStopped && each.type === 'ip') {
      const status = statusOf.get(each.row.key)?.status;
      if (status === 'stopped' || status === 'protected') return false;
    }
    return true;
  });
  const rows = showAll ? visible : visible.slice(0, SHOWN);

  const columns: Column<Suspect>[] = [
    {
      key: 'type',
      header: 'Kind',
      sortValue: (each) => each.type,
      cell: (each) => <span className="badge badge-muted">{SUSPECT_LABELS[each.type]}</span>,
    },
    {
      key: 'name',
      header: 'Who or what',
      rowHeader: true,
      sortValue: (each) => each.row.key,
      cell: (each) =>
        each.type === 'host' ? (
          <SiteName host={each.row.key} />
        ) : (
          <KeyCell
            dimension={each.type}
            row={each.row}
            status={each.type === 'ip' ? statusOf.get(each.row.key) : undefined}
          />
        ),
    },
    {
      key: 'why',
      header: 'Why it is here',
      cell: (each) => <span className="text-sm">{each.reason}</span>,
    },
    {
      key: 'risk',
      header: 'Risk',
      sortValue: (each) => RISK_RANK[each.risk],
      cell: (each) => <RiskBadge risk={each.risk} why={each.reason} />,
    },
    {
      key: 'next',
      header: 'Next',
      cell: (each) => {
        if (each.type === 'ip' && actor !== undefined) {
          return <BanAction ip={each.row.key} actor={actor} />;
        }
        if (each.type === 'bot' && actor !== undefined) {
          return <BotActions botKey={each.row.key} actor={actor} />;
        }
        if (each.type === 'path' && canChange && blockablePath(each.row.key).ok) {
          return (
            <Link
              className="text-sm"
              to={blockRuleLink(each.row.key, {
                serverId: scope.serverId,
                groupId: scope.groupId,
              })}
            >
              Block this page…
            </Link>
          );
        }
        return null;
      },
    },
  ];

  return (
    <Card
      title="Worth a look"
      description="The addresses, pages, bots and sites that look worst in this range, in one list. Open a row for who and what is joined to it."
    >
      <div className="filters traffic-filters">
        <SelectField
          label="Kind"
          value={type}
          onChange={(event) => {
            setType(event.target.value as SuspectType | 'all');
            setShowAll(false);
          }}
        >
          <option value="all">Everything</option>
          {TYPES.map((each) => (
            <option key={each} value={each}>
              {each === 'ip' ? 'Addresses' : `${SUSPECT_LABELS[each]}s`}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Risk"
          value={minRisk}
          onChange={(event) => {
            setMinRisk(event.target.value as TrafficRisk);
          }}
        >
          {RISKS.map((risk) => (
            <option key={risk.value} value={risk.value}>
              {risk.label}
            </option>
          ))}
        </SelectField>
        <fieldset className="filter-checks">
          <legend className="visually-hidden">Only show</legend>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={notStopped}
              onChange={(event) => {
                setNotStopped(event.target.checked);
              }}
            />
            Addresses not stopped yet
          </label>
        </fieldset>
      </div>
      {!loaded && <div className="skeleton skeleton-block" aria-busy="true" aria-label="Loading" />}
      {loaded && (
        <DataTable
          caption="What is worth a look, worst first"
          columns={columns}
          rows={rows}
          rowKey={(each) => `${each.type}:${each.row.key}`}
          initialSort={{ key: 'risk', direction: 'desc' }}
          renderDetail={(each) => (
            <TrafficRowDetail
              dimension={each.type}
              row={each.row}
              scope={scope}
              blockPage={
                canChange
                  ? (path) =>
                      blockRuleLink(path, { serverId: scope.serverId, groupId: scope.groupId })
                  : undefined
              }
              addressAction={
                actor === undefined ? undefined : (ip) => <BanAction ip={ip} actor={actor} />
              }
            />
          )}
          empty={
            <EmptyState title="Nothing stands out">
              No address, page, bot or site looks worse than {minRisk} in this range.
            </EmptyState>
          }
        />
      )}
      {loaded && visible.length > SHOWN && (
        <div className="row">
          <button
            type="button"
            className="button button-secondary"
            onClick={() => {
              setShowAll(!showAll);
            }}
          >
            {showAll ? 'Show fewer' : `Show all ${String(visible.length)}`}
          </button>
        </div>
      )}
    </Card>
  );
}
